import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCountdown } from "@/hooks/useCountdown";
import ProductImage from "@/components/ProductImage";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, ArrowRight, Lock, LockOpen, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Cart() {
  const { user, authHeaders } = useAuth();
  const { format, code, lock, requestLock, clearLock, getRate, source, fetchedAt } = useCurrency();
  const [cart, setCart] = useState({ items: [], total_usd: 0 });
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [locking, setLocking] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cart`, { headers: authHeaders(), withCredentials: true });
      setCart(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (product_id) => {
    await axios.post(`${API}/cart/remove`, { product_id, qty_kg: 0 }, { headers: authHeaders(), withCredentials: true });
    load();
  };

  const checkout = async () => {
    setPayLoading(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/checkout/session`, { origin_url: origin }, { headers: authHeaders(), withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setPayLoading(false);
    }
  };

  const doLock = async () => {
    setLocking(true);
    try {
      await requestLock(cart.total_usd, authHeaders);
      toast.success(`Rate locked for 15 minutes in ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rate lock failed");
    }
    setLocking(false);
  };

  const activeLock = lock && lock.currency === code && cart.total_usd > 0;
  const countdown = useCountdown(activeLock ? lock.expires_at : null);
  const canOfferLock = code !== "USD" && cart.total_usd > 0;
  const liveRate = getRate();

  if (!user) return (
    <div className="min-h-screen pt-32 text-center">
      <p className="text-muted">Please sign in.</p>
      <Link to="/login" className="btn-primary inline-flex mt-6">Sign in</Link>
    </div>
  );

  return (
    <div className="min-h-screen pt-24" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <p className="section-label">Your vault</p>
        <h1 className="font-display text-5xl tracking-tighter mt-3">Review & checkout</h1>

        {loading ? (
          <div className="mt-10 text-muted">Loading...</div>
        ) : cart.items.length === 0 ? (
          <div className="mt-14 glass rounded-3xl p-20 text-center">
            <ShoppingBag size={40} weight="duotone" className="text-muted mx-auto" />
            <h3 className="font-display text-3xl mt-5">Your cart is empty</h3>
            <Link to="/shop" className="btn-primary inline-flex mt-7">Browse marketplace <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-12 gap-8">
            <div className="md:col-span-8 space-y-4">
              {cart.items.map((it) => (
                <motion.div key={it.product_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-5 flex items-center gap-5" data-testid={`cart-item-${it.product_id}`}>
                  {it.image && <ProductImage src={it.image} alt={it.branded_name} className="w-20 h-20 rounded-xl object-cover" />}
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold">{it.country} · Grade {it.grade}</div>
                    <h3 className="font-display text-xl text-cream">{it.branded_name}</h3>
                    <div className="text-sm text-muted mt-1">{it.qty_kg} kg × {format(it.price_usd)}/kg</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-saffron">{format(it.line_total)}</div>
                    <button onClick={() => remove(it.product_id)} className="text-xs text-muted hover:text-red-400 mt-2" data-testid={`remove-${it.product_id}`}>Remove</button>
                  </div>
                </motion.div>
              ))}

              {/* FX Rate Lock — only for non-USD */}
              {canOfferLock && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-2xl p-6 border border-gold/30"
                  data-testid="rate-lock-card"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <Lightning size={18} weight="fill" className="text-gold" />
                        <span className="section-label">Live FX · Rate Lock</span>
                      </div>
                      <p className="text-sm text-cream/90 mt-3 leading-relaxed max-w-md">
                        {activeLock ? (
                          <>Your rate is <span className="text-saffron font-medium">locked</span> at <span className="text-cream font-medium">1 USD = {lock.rate.toFixed(4)} {lock.currency}</span>. Complete checkout before the timer expires.</>
                        ) : (
                          <>The live rate is <span className="text-cream font-medium">1 USD = {liveRate.toFixed(4)} {code}</span>. Lock it for 15 minutes to protect against FX drift during checkout.</>
                        )}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-muted mt-2">
                        Source: {source} · {fetchedAt ? `updated ${new Date(fetchedAt).toLocaleTimeString()}` : "live"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {activeLock ? (
                        <>
                          <div className="flex items-center gap-2 bg-saffron/15 border border-saffron/40 rounded-full px-4 py-1.5" data-testid="lock-countdown">
                            <Lock size={14} weight="fill" className="text-saffron" />
                            <span className="font-mono text-sm text-saffron">{countdown.label}</span>
                          </div>
                          <button onClick={clearLock} className="text-xs text-muted hover:text-red-400 flex items-center gap-1" data-testid="lock-release">
                            <LockOpen size={12} /> Release lock
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={doLock}
                          disabled={locking}
                          className="bg-gold/10 border border-gold/40 text-gold hover:bg-gold hover:text-ink-950 transition-colors rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2 disabled:opacity-50"
                          data-testid="lock-btn"
                        >
                          {locking ? <ArrowsClockwise className="animate-spin" size={14} /> : <Lock size={14} weight="fill" />}
                          {locking ? "Locking..." : "Lock rate · 15 min"}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="md:col-span-4">
              <div className="glass rounded-2xl p-7 sticky top-24">
                <h3 className="font-display text-2xl text-cream">Order summary</h3>
                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between text-cream/80"><span>Subtotal</span><span>{format(cart.total_usd)}</span></div>
                  <div className="flex justify-between text-muted"><span>Shipping</span><span>Calculated at origin</span></div>
                  <div className="flex justify-between text-muted"><span>Escrow</span><span>Included</span></div>
                  {activeLock && (
                    <div className="flex justify-between text-saffron"><span className="flex items-center gap-1"><Lock size={12} weight="fill" />Rate locked</span><span>{countdown.label}</span></div>
                  )}
                </div>
                <div className="h-px bg-white/5 my-5" />
                <div className="flex justify-between items-baseline">
                  <span className="text-muted text-sm">Total · {code}</span>
                  <span className="font-display text-4xl text-gradient-gold">{format(cart.total_usd)}</span>
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted mt-3 text-center">Charged in USD · ${cart.total_usd.toFixed(2)}</p>
                <button onClick={checkout} disabled={payLoading} className="btn-primary w-full justify-center mt-6" data-testid="checkout-btn">
                  {payLoading ? "Redirecting..." : "Secure checkout"} <ArrowRight size={14} />
                </button>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted mt-4 text-center">Powered by Stripe · Funds in escrow</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
